import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Launch as LaunchIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  ContentCopy as ContentCopyIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { UnifiedFeedback } from '../../types/unifiedFeedback';
import { getFeedbackExternalUrl } from '../../utils/feedbackUrls';

export interface FeedbackActionButtonsProps {
  feedback: UnifiedFeedback;
  onViewDetail: (feedback: UnifiedFeedback) => void;
  onEditStatus?: (feedback: UnifiedFeedback) => void;
  onViewHistory?: (feedback: UnifiedFeedback) => void;
  onEditMetadata?: (feedback: UnifiedFeedback) => void;
  onViewGcc?: (feedback: UnifiedFeedback) => void;
  onCopy?: (feedback: UnifiedFeedback) => void;
  showMetadataEdit?: boolean;
  layout?: 'row' | 'column';
  compact?: boolean;
}

export const FeedbackActionButtons: React.FC<FeedbackActionButtonsProps> = ({
  feedback,
  onViewDetail,
  onEditStatus,
  onViewHistory,
  onEditMetadata,
  onViewGcc,
  onCopy,
  showMetadataEdit = true,
  layout = 'row',
  compact = false,
}) => {
  const externalUrl = getFeedbackExternalUrl(feedback);
  const isGcc = feedback.platform === 'gcc' && feedback.brand_identifier;
  const canEditMetadata =
    showMetadataEdit &&
    (feedback.platform === 'instagram' ||
      feedback.platform === 'tiktok' ||
      feedback.platform === 'facebook' ||
      feedback.platform === 'dm');

  return (
    <Box
      display="flex"
      flexDirection={layout === 'column' ? 'column' : 'row'}
      alignItems="center"
      flexShrink={0}
      gap={compact ? 0 : 0.5}
      sx={
        compact
          ? {
              bgcolor: 'action.hover',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              px: 0.25,
              py: 0.25,
              alignSelf: 'flex-start',
            }
          : undefined
      }
    >
      <Tooltip title="View details">
        <IconButton size="small" onClick={() => onViewDetail(feedback)}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {externalUrl && (
        <Tooltip title="Open original feedback">
          <IconButton
            size="small"
            component="a"
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <LaunchIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {isGcc && onViewGcc && (
        <Tooltip title="View GCC details">
          <IconButton size="small" onClick={() => onViewGcc(feedback)}>
            <LaunchIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {onEditStatus && (
        <Tooltip title="Update status">
          <IconButton size="small" onClick={() => onEditStatus(feedback)}>
            <AssignmentIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {onViewHistory && (
        <Tooltip title="Status history">
          <IconButton size="small" onClick={() => onViewHistory(feedback)}>
            <HistoryIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {canEditMetadata && onEditMetadata && (
        <Tooltip title="Edit metadata">
          <IconButton size="small" onClick={() => onEditMetadata(feedback)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {onCopy && (
        <Tooltip title="Copy text">
          <IconButton size="small" onClick={() => onCopy(feedback)}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
